/**
 * Renders ONE backend message (§5) from the Product Catalog Control Center AI
 * chatbot (Brisque AI), switching on `kind`. Card edits/confirm/sync/fix actions
 * dispatch through the `usePCChatbot` hook. Channel ids are resolved to names via
 * the fetched channels list (§4.8). Purely presentational otherwise.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PCChatbot } from '../hooks/usePCChatbot';
import { Rich, Icon, Chip, Pill, PrimaryBtn, GhostBtn, FONT } from './atoms';
import type {
    PCMessage,
    PCActionCard,
    PCActionRow,
    PCStateCard,
    PCDiagnosticCard,
    PCDiagnosticMultiCard,
    PCSyncCard,
    PCDuration,
    PCFix,
    PCBotTextMessage,
} from '../../../interface/ProductCatalogChatbotInterface';

const DURATION_PRESETS: PCDuration[] = [
    { mode: 'next_session', label: 'Until next session' },
    { mode: 'specific_time', until: '11:00 PM', atTime: '23:00', label: 'Until close (11:00 PM)' },
    { mode: 'specific_time', until: '9:00 PM', atTime: '21:00', label: 'Until 9:00 PM' },
    { mode: 'end_of_day', label: 'End of day' },
    { mode: 'manual', label: 'Until manually re-enabled' },
];

const toneColor: Record<string, { c: string; bg: string; icon: string }> = {
    warn: { c: 'var(--orange)', bg: 'rgba(217,119,6,0.07)', icon: 'info' },
    error: { c: 'var(--red)', bg: 'rgba(220,38,38,0.07)', icon: 'error' },
    success: { c: 'var(--green)', bg: 'rgba(22,163,74,0.07)', icon: 'check_circle' },
};

/**
 * Card wrapper. `spent` greys the whole card and blocks all pointer interaction — used
 * once an actionable card has been acted on (confirmed/cancelled/picked/synced) or has an
 * action in flight, so the same action can never be triggered twice (§Item 1). It stays
 * fully readable (the record of what was done) but is visibly, unmistakably inert.
 */
const CardShell: React.FC<{ children: React.ReactNode; accent?: string; spent?: boolean }> = ({ children, accent = 'var(--accent)', spent }) => (
    <div
        aria-disabled={spent || undefined}
        style={{
            border: '1px solid var(--card-border)', borderLeft: `3px solid ${accent}`,
            borderRadius: 'var(--radius-card)', background: 'var(--surface)', boxShadow: 'var(--card-shadow)',
            overflow: 'hidden', width: '100%', maxWidth: 460,
            opacity: spent ? 0.62 : 1,
            pointerEvents: spent ? 'none' : undefined,
            filter: spent ? 'grayscale(0.35)' : undefined,
            transition: 'opacity .2s ease, filter .2s ease',
        }}
    >
        {children}
    </div>
);

const Head: React.FC<{ icon: string; title?: string; subtitle?: string; accent?: string }> = ({ icon, title, subtitle, accent = 'var(--accent)' }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--divider)' }}>
        <div style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--accent-soft, rgba(210,58,59,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name={icon} size={17} color={accent} />
        </div>
        <div style={{ minWidth: 0 }}>
            {title && <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--t1)', fontFamily: FONT }}>{title}</div>}
            {subtitle && <div style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT, marginTop: 1 }}>{subtitle}</div>}
        </div>
    </div>
);

/** One-tap fix buttons (§5.2/5.4/5.5) — send the instruction as a new message. */
const FixButtons: React.FC<{ fixes?: PCFix[]; onSend: (text: string) => void }> = ({ fixes, onSend }) =>
    fixes && fixes.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 12px' }}>
            {fixes.map((f, i) => (
                <GhostBtn key={i} icon="build" small onClick={() => onSend(f.instruction)}>{f.label}</GhostBtn>
            ))}
        </div>
    ) : null;

/* ─────────────────────────── bot-text (§5.2) ─────────────────────────── */

const BotText: React.FC<{ msg: PCBotTextMessage; onSend: (text: string) => void }> = ({ msg, onSend }) => {
    const t = msg.tone ? toneColor[msg.tone] : undefined;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: 'linear-gradient(145deg, var(--accent), #B22830)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <Icon name="auto_awesome" size={14} color="#fff" />
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.5, color: t ? t.c : 'var(--t1)', fontFamily: FONT, background: t ? t.bg : 'transparent', padding: t ? '9px 12px' : '3px 0', borderRadius: t ? 12 : 0, border: t ? `1px solid ${t.c}22` : 'none' }}>
                    {t && <Icon name={t.icon} size={14} color={t.c} style={{ verticalAlign: -2, marginRight: 5 }} />}
                    <Rich text={msg.text} />
                </div>
            </div>
            {msg.fixes && msg.fixes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginLeft: 35 }}>
                    {msg.fixes.map((f, i) => (
                        <GhostBtn key={i} icon="bolt" small onClick={() => onSend(f.instruction)}>{f.label}</GhostBtn>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ─────────────────────────── Action card (§6) ─────────────────────────── */

const TYPE_META: Record<string, { label: string; accent: string; icon: string }> = {
    availability: { label: 'Availability', accent: 'var(--orange)', icon: 'inventory' },
    visibility: { label: 'Visibility', accent: '#7C3AED', icon: 'visibility' },
    session: { label: 'Session', accent: '#0EA5E9', icon: 'schedule' },
};

const opTitle = (row: PCActionRow): string => {
    if (row.type === 'availability') return row.op === 'restore' ? 'Restore (un-86)' : 'Mark out of stock (86)';
    if (row.type === 'visibility') return row.op === 'show' ? 'Show on channel' : 'Hide on channel';
    return 'Change sessions';
};

const RowView: React.FC<{
    row: PCActionRow; editable: boolean; multi: boolean; chat: PCChatbot;
    onChannels: (ids: string[]) => void; onDuration: (d: PCDuration) => void; onRemove: () => void;
}> = ({ row, editable, multi, chat, onChannels, onDuration, onRemove }) => {
    const meta = TYPE_META[row.type] ?? TYPE_META.availability;
    const allChannels = chat.state.channels;
    // Channels the server froze for this row (e.g. availability on a channel where the item
    // isn't even enabled) must be shown as DISABLED chips — never clickable — so the card's
    // controls can't contradict its own "can't change X here" notice (§blocked-channel UX).
    const blockedSet = new Set((row.blockedChannels ?? []).map((b) => b.channel));
    const blockedReason = (id: string): string | undefined => row.blockedChannels?.find((b) => b.channel === id)?.reason;
    // Channels already in the requested state — a no-op with nothing to write. Also locked, but
    // shown as "already done" (a satisfied ✓ chip) rather than a blocked one, so the user isn't
    // invited to toggle a channel that would change nothing (e.g. "enable on Delivery" when it's
    // already enabled there).
    const alreadySet = new Set(row.alreadyChannels ?? []);
    const verb = row.type === 'availability'
        ? (row.op === 'restore' ? 'in stock' : 'out of stock')
        : (row.op === 'show' ? 'shown' : 'hidden');
    const toggle = (id: string) => {
        if (blockedSet.has(id) || alreadySet.has(id)) return; // frozen / no-op — ignore taps
        const has = row.channels.includes(id);
        onChannels(has ? row.channels.filter((x) => x !== id) : [...row.channels, id]);
    };
    return (
        <div style={{ padding: '11px 14px', borderTop: '1px solid var(--divider)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, background: 'var(--bg)', color: meta.accent, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase', fontFamily: FONT }}>
                        <Icon name={meta.icon} size={12} color={meta.accent} />{meta.label}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)', fontFamily: FONT }}>{row.entityName}</span>
                </div>
                {multi && editable && (
                    <button type="button" onClick={onRemove} title="Remove" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--t3)', display: 'flex' }}>
                        <Icon name="close" size={16} />
                    </button>
                )}
            </div>

            <div style={{ fontSize: 12, color: 'var(--t2)', fontFamily: FONT, marginTop: 4 }}>{opTitle(row)}</div>

            {row.type === 'visibility' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '6px 9px', borderRadius: 8, background: 'rgba(124,58,237,0.07)', color: '#7C3AED', fontSize: 11.5, fontWeight: 600, fontFamily: FONT }}>
                    <Icon name="push_pin" size={13} /> This changes where it appears permanently — not just today.
                </div>
            )}

            {row.type === 'session' ? (
                row.sessionLink && (
                    <div style={{ marginTop: 6, fontSize: 12, fontFamily: FONT, color: 'var(--t2)' }}>
                        {row.sessionLink.itemName || row.entityName}
                        {row.sessionLink.sessionName ? ` → ${row.sessionLink.sessionName}` : ''}
                    </div>
                )
            ) : (
                <>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 9, fontFamily: FONT }}>Channels</div>
                    {editable ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                            {allChannels.map((c) => {
                                const blocked = blockedSet.has(c.id);
                                const already = !blocked && alreadySet.has(c.id);
                                const locked = blocked || already;
                                const tip = blocked
                                    ? (blockedReason(c.id) || "Can't change this channel here")
                                    : already
                                        ? `Already ${verb} here — nothing to change`
                                        : undefined;
                                return (
                                    <span key={c.id} title={tip} style={{ display: 'inline-flex' }}>
                                        <Chip
                                            label={c.name}
                                            // "already" chips read as satisfied (soft green + check); "blocked" as inert.
                                            active={already || row.channels.includes(c.id)}
                                            tone={already ? 'success' : 'accent'}
                                            onClick={locked ? undefined : () => toggle(c.id)}
                                            disabled={locked}
                                            icon={blocked ? 'visibility_off' : already ? 'check' : undefined}
                                            small
                                        />
                                    </span>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ fontSize: 12, color: 'var(--t1)', fontFamily: FONT, marginTop: 3 }}>{row.channels.map(chat.channelName).join(', ')}</div>
                    )}
                </>
            )}

            {row.type === 'availability' && row.op === 'oos' && (
                <>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 9, fontFamily: FONT }}>Duration</div>
                    {editable ? (
                        <select
                            value={row.duration?.label ?? ''}
                            onChange={(e) => {
                                const found = DURATION_PRESETS.find((d) => d.label === e.target.value) ?? DURATION_PRESETS[0];
                                onDuration(found);
                            }}
                            style={{ marginTop: 6, padding: '6px 8px', borderRadius: 'var(--radius-input)', border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--t1)', fontSize: 12, fontFamily: FONT, fontWeight: 600, cursor: 'pointer' }}
                        >
                            {row.duration && !DURATION_PRESETS.some((d) => d.label === row.duration!.label) && (
                                <option value={row.duration.label}>{row.duration.label}</option>
                            )}
                            {DURATION_PRESETS.map((d) => <option key={d.label} value={d.label}>{d.label}</option>)}
                        </select>
                    ) : (
                        <div style={{ fontSize: 12, color: 'var(--t1)', fontFamily: FONT, marginTop: 3 }}>{row.duration?.label}</div>
                    )}
                </>
            )}

            {row.blockedChannels && row.blockedChannels.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <Pill tone="bad"><Icon name="visibility_off" size={12} /> Can't change {row.blockedChannels.map((b) => chat.channelName(b.channel)).join(', ')}</Pill>
                    {row.blockedChannels[0]?.reason && <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 3, fontFamily: FONT }}>{row.blockedChannels[0].reason}</div>}
                </div>
            )}
            {row.note && <div style={{ marginTop: 8 }}><Pill tone="info"><Icon name="info" size={12} /> {row.note}</Pill></div>}
            {row.warning && <div style={{ marginTop: 8 }}><Pill tone="warn"><Icon name="warning" size={12} /> {row.warning}</Pill></div>}
            {row.withheld && <div style={{ marginTop: 8 }}><Pill tone="bad"><Icon name="lock" size={12} /> Withheld</Pill></div>}
        </div>
    );
};

/** The "which one did you mean?" picker, paginated at PAGE_SIZE so a query matching many items
 *  (e.g. "fries" → 8) shows a scannable page at a time with Prev/Next, not a wall of rows. */
const DISAMBIG_PAGE_SIZE = 5;
const DisambiguationView: React.FC<{ card: PCActionCard; chat: PCChatbot }> = ({ card, chat }) => {
    const candidates = card.candidates ?? [];
    const [page, setPage] = React.useState(0);
    const pageCount = Math.ceil(candidates.length / DISAMBIG_PAGE_SIZE);
    const safePage = Math.min(page, Math.max(0, pageCount - 1));
    const start = safePage * DISAMBIG_PAGE_SIZE;
    const shown = candidates.slice(start, start + DISAMBIG_PAGE_SIZE);

    const pagerBtn = (disabled: boolean): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', gap: 3, padding: '5px 9px', borderRadius: 8,
        border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--t2)',
        fontFamily: FONT, fontSize: 12, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
    });

    // A pick in flight (cardActionLoading) freezes the whole picker so a second candidate
    // can't be tapped before the first resolves — the optimistic patch then retires it and
    // this view stops rendering entirely (status flips off 'disambiguation').
    const busy = chat.state.cardActionLoading;

    return (
        <CardShell accent="#0EA5E9" spent={busy}>
            <Head
                icon="alt_route"
                title={card.title || 'Which one did you mean?'}
                subtitle={candidates.length > DISAMBIG_PAGE_SIZE ? `${candidates.length} matches — pick the one you meant` : 'Pick the one you meant'}
                accent="#0EA5E9"
            />
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {shown.map((c) => (
                    <button key={c.id} type="button" disabled={busy} onClick={() => chat.pickCandidate(card.id, c.id)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--card-border)', background: 'var(--surface)', cursor: 'pointer', fontFamily: FONT }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Icon name={c.kind === 'category' ? 'category' : 'lunch_dining'} size={16} color="var(--t3)" />
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{c.name}</span>
                        </span>
                        {c.subtitle && <span style={{ fontSize: 11, color: 'var(--t3)' }}>{c.subtitle}</span>}
                    </button>
                ))}

                {pageCount > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4 }}>
                        <button type="button" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} style={pagerBtn(safePage === 0)}>
                            <Icon name="chevron_left" size={15} /> Prev
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT }}>Page {safePage + 1} of {pageCount}</span>
                        <button type="button" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} style={pagerBtn(safePage >= pageCount - 1)}>
                            Next <Icon name="chevron_right" size={15} />
                        </button>
                    </div>
                )}

                <div style={{ fontSize: 11, color: 'var(--t3)', fontFamily: FONT, padding: '2px 4px' }}>None of these? Just type a more specific instruction.</div>
            </div>
        </CardShell>
    );
};

const ActionCardView: React.FC<{ card: PCActionCard; chat: PCChatbot }> = ({ card, chat }) => {
    if (card.status === 'disambiguation' && card.candidates) {
        return <DisambiguationView card={card} chat={chat} />;
    }

    const editable = card.status === 'pending';
    const cancelled = card.status === 'cancelled';
    const committed = card.status === 'committed';
    const multi = card.rows.length > 1;
    const noChannels = card.rows.some((r) => r.type !== 'session' && r.channels.length === 0);
    // Once confirmed or cancelled the card is a spent record — greyed and fully inert so its
    // Confirm/Cancel/edit controls can never be triggered again (§Item 1).
    const spent = committed || cancelled;

    return (
        <CardShell accent={cancelled ? 'var(--t3)' : 'var(--accent)'} spent={spent}>
            <Head
                icon={committed ? 'check_circle' : cancelled ? 'cancel' : 'bolt'}
                title={card.title || 'Review this change'}
                subtitle={multi ? `${card.rows.length} changes · each logged separately` : undefined}
            />
            {card.rows.map((row) => (
                <RowView
                    key={row.id} row={row} editable={editable} multi={multi} chat={chat}
                    onChannels={(ids) => chat.setRowChannels(card.id, row.id, ids)}
                    onDuration={(d) => chat.setRowDuration(card.id, row.id, d)}
                    onRemove={() => chat.removeRow(card.id, row.id)}
                />
            ))}
            <div style={{ padding: '11px 14px', borderTop: '1px solid var(--divider)', display: 'flex', alignItems: 'center', gap: 8 }}>
                {committed ? (
                    <Pill tone="ok"><Icon name="check" size={13} /> Confirmed</Pill>
                ) : cancelled ? (
                    <Pill tone="info">Not applied</Pill>
                ) : (
                    <>
                        <PrimaryBtn icon="check" onClick={() => chat.confirm(card.id)} disabled={noChannels || chat.state.cardActionLoading}>Confirm</PrimaryBtn>
                        <GhostBtn onClick={() => chat.cancelCard(card.id)}>Cancel</GhostBtn>
                        {noChannels && <span style={{ fontSize: 11, color: 'var(--orange)', fontFamily: FONT }}>Select at least one channel</span>}
                    </>
                )}
            </div>
        </CardShell>
    );
};

/* ─────────────────────────── State card (§5.3) ─────────────────────────── */

const Dot: React.FC<{ on: boolean; color: string }> = ({ on, color }) => (
    <span style={{ width: 9, height: 9, borderRadius: '50%', background: on ? color : 'transparent', border: `1.5px solid ${on ? color : 'var(--card-border)'}`, display: 'inline-block' }} />
);

const StateCardView: React.FC<{ card: PCStateCard; chat: PCChatbot }> = ({ card, chat }) => (
    <CardShell accent="#0EA5E9">
        <Head icon="travel_explore" title={card.title} subtitle={card.subtitle} accent="#0EA5E9" />
        <div style={{ padding: 12 }}>
            {card.mode === 'entity-grid' && card.cells && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
                    <thead>
                        <tr style={{ fontSize: 10.5, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 700 }}>Channel</th>
                            <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700 }}>Visible</th>
                            <th style={{ textAlign: 'center', padding: '4px 6px', fontWeight: 700 }}>Available</th>
                        </tr>
                    </thead>
                    <tbody>
                        {card.cells.map((cell) => (
                            <tr key={cell.channel} style={{ borderTop: '1px solid var(--divider)' }}>
                                <td style={{ padding: '7px 6px', fontSize: 12.5, fontWeight: 600, color: 'var(--t1)' }}>{chat.channelName(cell.channel)}</td>
                                <td style={{ textAlign: 'center', padding: '7px 6px' }}><Dot on={cell.visible} color="#7C3AED" /></td>
                                <td style={{ textAlign: 'center', padding: '7px 6px' }}><Dot on={cell.available} color="var(--green)" /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {card.mode === 'item-list' && card.items && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {card.items.map((it, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontFamily: FONT }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{it.name}</span>
                            {it.detail && <Pill tone={it.tone ?? 'info'}>{it.detail}</Pill>}
                        </div>
                    ))}
                </div>
            )}
            {card.mode === 'session' && card.sessionLines && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {card.sessionLines.map((l, i) => (
                        <div key={i} style={{ fontFamily: FONT }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{l.label}</div>
                            <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 1 }}>{l.detail}</div>
                        </div>
                    ))}
                </div>
            )}
            {card.footnote && <div style={{ marginTop: 11, fontSize: 11, color: 'var(--t3)', fontFamily: FONT, lineHeight: 1.4 }}>{card.footnote}</div>}
        </div>
    </CardShell>
);

/* ─────────────────────────── Diagnostic card (§5.4) ─────────────────────────── */

const DiagnosticCardView: React.FC<{ card: PCDiagnosticCard; chat: PCChatbot; onSend: (text: string) => void }> = ({ card, chat, onSend }) => {
    const accent = card.verdict === 'showing' ? 'var(--green)' : 'var(--orange)';
    return (
        <CardShell accent={accent}>
            <Head icon={card.verdict === 'showing' ? 'check_circle' : 'troubleshoot'} title={`${card.entityName} on ${chat.channelName(card.channel)}`} subtitle="Resolution chain" accent={accent} />
            <div style={{ padding: '10px 14px 4px', fontSize: 12.5, color: 'var(--t2)', fontFamily: FONT, lineHeight: 1.45 }}>
                <Rich text={card.headline} />
            </div>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {card.steps.map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 8px', borderRadius: 8, background: s.primary ? 'rgba(217,119,6,0.08)' : 'transparent', fontFamily: FONT }}>
                        <Icon name={s.passed ? 'check_circle' : 'cancel'} size={16} color={s.passed ? 'var(--green)' : 'var(--red)'} style={{ marginTop: 1 }} />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--t1)' }}>{s.label}{s.primary && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: 'var(--orange)' }}>BLOCKER</span>}</div>
                            {s.detail && <div style={{ fontSize: 11.5, color: 'var(--t3)' }}>{s.detail}</div>}
                        </div>
                    </div>
                ))}
            </div>
            <FixButtons fixes={card.fixes} onSend={onSend} />
        </CardShell>
    );
};

/* ─────────────────────────── Diagnostic-multi card (§5.5) ─────────────────────────── */

const DiagnosticMultiCardView: React.FC<{ card: PCDiagnosticMultiCard; chat: PCChatbot; onSend: (text: string) => void }> = ({ card, chat, onSend }) => (
    <CardShell accent="#0EA5E9">
        <Head icon="troubleshoot" title={card.entityName} subtitle="Per-channel status" accent="#0EA5E9" />
        <div style={{ padding: '10px 14px 4px', fontSize: 12.5, color: 'var(--t2)', fontFamily: FONT, lineHeight: 1.45 }}>
            <Rich text={card.headline} />
        </div>
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {card.rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 8px', borderTop: i ? '1px solid var(--divider)' : 'none', fontFamily: FONT }}>
                    <Icon name={r.verdict === 'showing' ? 'check_circle' : 'cancel'} size={16} color={r.verdict === 'showing' ? 'var(--green)' : 'var(--red)'} style={{ marginTop: 1 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--t1)' }}>{chat.channelName(r.channel)}</div>
                        {r.reason && <div style={{ fontSize: 11.5, color: 'var(--t3)' }}>{r.reason}</div>}
                    </div>
                </div>
            ))}
        </div>
        <FixButtons fixes={card.fixes} onSend={onSend} />
    </CardShell>
);

/* ─────────────────────────── Sync card (§7) ─────────────────────────── */

const groupLabel: Record<string, string> = { device: 'Devices', integration: 'Integrations', direct: 'Direct channels', third_party: 'Third parties' };

const SyncCardView: React.FC<{ msgId: string; card: PCSyncCard; conversationId: string; chat: PCChatbot }> = ({ msgId, card, conversationId, chat }) => {
    const navigate = useNavigate();
    // The saga now owns the whole trigger → countdown → results flow (runSyncCountdown in
    // ProductCatalogChatbotSaga.ts) and ticks `card.summary` live every second via
    // CHATBOT_SYNC_TICK — no component-side polling needed; this just renders whatever the
    // store currently holds.
    const showResults = card.status === 'results' && card.results;
    const groups = Array.from(new Set((card.results ?? []).map((r) => r.group ?? 'other')));
    // A finished sync — auto-synced, or results with nothing left to retry — is a spent
    // record: greyed and inert so "Sync now" / "Retry" can't be re-triggered (§Item 1). An
    // 'offer' stays live (there's a sync to run) and 'results' WITH failures stays live (Retry).
    const spent = card.status === 'auto' || (card.status === 'results' && !card.hasFailures);
    // A 'running' card with no active countdown (the saga only runs one live; on reload there
    // is none) is a stalled sync we can't auto-resume — offer a manual status check instead of
    // a dead "Working…" pill. `syncing` is true only while an active saga countdown is ticking.
    const stalledRunning = card.status === 'running' && !chat.state.syncing;
    // Once a sync has actually happened (running/results/auto), surface a link to the full Sync
    // History page. Rendered OUTSIDE the (frozen) card so it stays clickable even when spent.
    const showHistoryLink = card.status !== 'offer';

    return (
      <div style={{ width: '100%', maxWidth: 460 }}>
        <CardShell accent="var(--green)" spent={spent}>
            <Head
                icon={card.status === 'auto' ? 'cloud_done' : card.status === 'running' ? 'sync' : showResults ? 'fact_check' : 'sync'}
                title={card.status === 'auto' ? 'Synced automatically' : card.status === 'running' ? 'Syncing…' : showResults ? 'Sync results' : 'Sync changes'}
                subtitle={card.status === 'offer' && card.changeCount ? `${card.changeCount} pending` : undefined}
                accent="var(--green)"
            />
            <div style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--t2)', fontFamily: FONT, lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                <Rich text={card.summary} />
            </div>

            {showResults && (
                <div style={{ padding: '0 14px 10px' }}>
                    {groups.map((g) => {
                        const rows = card.results!.filter((r) => (r.group ?? 'other') === g);
                        if (!rows.length) return null;
                        return (
                            <div key={g} style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, fontFamily: FONT }}>{groupLabel[g] ?? g}</div>
                                {rows.map((r, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: '5px 0', fontFamily: FONT }}>
                                        <span style={{ fontSize: 12.5, color: 'var(--t1)', fontWeight: 600 }}>{r.label}</span>
                                        <Icon
                                            name={r.status === 'success' ? 'check_circle' : r.status === 'failed' ? 'error' : 'schedule'}
                                            size={16}
                                            color={r.status === 'success' ? 'var(--green)' : r.status === 'failed' ? 'var(--red)' : 'var(--orange)'}
                                        />
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}

            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--divider)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {card.status === 'offer' && (
                    <PrimaryBtn icon="sync" onClick={() => chat.runSync(conversationId, msgId)} disabled={chat.state.syncing}>Sync now</PrimaryBtn>
                )}
                {card.status === 'running' && (
                    stalledRunning ? (
                        // Resumed after a reload mid-sync — the live countdown saga is gone, so
                        // give the user a way to pull the outcome rather than stranding them.
                        <>
                            <Pill tone="warn"><Icon name="sync" size={13} /> In progress</Pill>
                            <GhostBtn icon="refresh" small onClick={() => chat.fetchSyncResults(conversationId, msgId)}>Check status</GhostBtn>
                        </>
                    ) : (
                        <Pill tone="warn"><Icon name="sync" size={13} /> Working…</Pill>
                    )
                )}
                {card.status === 'auto' && <Pill tone="ok"><Icon name="check" size={13} /> Up to date</Pill>}
                {showResults && (
                    card.hasFailures ? (
                        <>
                            <Pill tone="warn"><Icon name="warning" size={13} /> Some targets failed</Pill>
                            <GhostBtn icon="refresh" small onClick={() => chat.retrySync(conversationId, msgId, card.results ?? [])}>Retry failed</GhostBtn>
                        </>
                    ) : (
                        <Pill tone="ok"><Icon name="check" size={13} /> Synced everywhere</Pill>
                    )
                )}
            </div>
        </CardShell>
        {showHistoryLink && (
            // Navigation aid — kept outside the (possibly spent/frozen) card so it stays
            // clickable, and styled as a link, not a re-runnable action.
            <button
                type="button"
                onClick={() => navigate('/product-catalog/sync-history')}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 7, marginLeft: 2,
                    border: 'none', background: 'transparent', color: 'var(--accent)', cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, fontFamily: FONT, padding: 0,
                }}
            >
                <Icon name="history" size={14} color="var(--accent)" /> View Sync History
            </button>
        )}
      </div>
    );
};

/* ─────────────────────────── Dispatcher ─────────────────────────── */

export const PCMessageView: React.FC<{
    msg: PCMessage;
    chat: PCChatbot;
    conversationId: string;
    onSend: (text: string) => void;
}> = ({ msg, chat, conversationId, onSend }) => {
    switch (msg.kind) {
        case 'bot-text':
            return <div style={{ margin: '4px 0' }}><BotText msg={msg} onSend={onSend} /></div>;
        case 'state':
            return <div style={{ margin: '6px 0' }}><StateCardView card={msg.card} chat={chat} /></div>;
        case 'diagnostic':
            return <div style={{ margin: '6px 0' }}><DiagnosticCardView card={msg.card} chat={chat} onSend={onSend} /></div>;
        case 'diagnostic-multi':
            return <div style={{ margin: '6px 0' }}><DiagnosticMultiCardView card={msg.card} chat={chat} onSend={onSend} /></div>;
        case 'action':
            return <div style={{ margin: '6px 0' }}><ActionCardView card={msg.card} chat={chat} /></div>;
        case 'sync':
            return <div style={{ margin: '6px 0' }}><SyncCardView msgId={msg.id} card={msg.card} conversationId={conversationId} chat={chat} /></div>;
        default:
            return null;
    }
};
